import React from 'react';
import {
  TaskyEmptyState,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';
import { AdminVideoTemplate } from '../../services/api/adminVideoTemplateService';

interface VideoTemplateListProps {
  templates: AdminVideoTemplate[];
  onEdit: (template: AdminVideoTemplate) => void;
  onDelete: (id: number, name: string) => void;
  onToggle: (id: number) => void;
}

// 下を流れる列の上に描くので、使う側で背景を塗る。左の影で境目を見せる
const STICKY_ACTIONS_CLASS = 'lg:sticky right-0 lg:shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]';

const VideoTemplateList: React.FC<VideoTemplateListProps> = ({
  templates,
  onEdit,
  onDelete,
  onToggle
}) => {
  const sortedTemplates = [...templates].sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });

  const truncate = (text: string, maxLength: number): string => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (templates.length === 0) {
    return (
      <TaskyEmptyState title="動画テンプレートデータがありません。" />
    );
  }

  return (
    <TaskyTable>
      <TaskyTableHead>
        <TaskyTableRow hoverable={false}>
          <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
          <TaskyTableHeaderCell>カテゴリ</TaskyTableHeaderCell>
          <TaskyTableHeaderCell>名前</TaskyTableHeaderCell>
          <TaskyTableHeaderCell align="center">アイコン</TaskyTableHeaderCell>
          <TaskyTableHeaderCell align="center">サムネイル</TaskyTableHeaderCell>
          <TaskyTableHeaderCell>説明</TaskyTableHeaderCell>
          <TaskyTableHeaderCell>プロンプト</TaskyTableHeaderCell>
          <TaskyTableHeaderCell className="min-w-[50px]">グラデーション</TaskyTableHeaderCell>
          <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
          <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
          {/* 見出し・名前を縮めない分、PC 幅でも表が枠より広い。操作列は右端に留めて、横にスクロールしなくても押せるようにする */}
          <TaskyTableHeaderCell className={`${STICKY_ACTIONS_CLASS} bg-gray-50`}>アクション</TaskyTableHeaderCell>
        </TaskyTableRow>
      </TaskyTableHead>
      <TaskyTableBody>
        {sortedTemplates.map((template) => (
          <TaskyTableRow key={template.id} className="group">
            {/* 見出しを折り返さない分ほかの列が細くなる。ID・名前は1文字ずつ割れないよう折り返さず、説明は数文字幅まで縮めない */}
            <TaskyTableCell className="text-gray-500 font-mono whitespace-nowrap">{template.id}</TaskyTableCell>
            <TaskyTableCell>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primary whitespace-nowrap">
                {template.category}
              </span>
            </TaskyTableCell>
            <TaskyTableCell className="font-medium whitespace-nowrap">{template.name}</TaskyTableCell>
            <TaskyTableCell align="center" className="text-lg">
              {template.icon || '-'}
            </TaskyTableCell>
            <TaskyTableCell align="center">
              {template.thumbnail_url ? (
                <img
                  src={template.thumbnail_url}
                  alt={`${template.name}のサムネイル`}
                  loading="lazy"
                  className="mx-auto h-10 w-16 rounded-tasky-xs border border-warm-border object-cover"
                />
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </TaskyTableCell>
            <TaskyTableCell className="text-gray-600 min-w-[150px]">
              <span title={template.description || ''}>
                {truncate(template.description || '', 30)}
              </span>
            </TaskyTableCell>
            <TaskyTableCell>
              <code
                className="inline-block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-xs bg-warm-surface text-gray-700 px-1.5 py-0.5 rounded-tasky-xs"
                title={template.prompt_template}
              >
                {truncate(template.prompt_template, 40)}
              </code>
            </TaskyTableCell>
            <TaskyTableCell>
              {template.gradient ? (
                <div
                  className="w-10 h-6 rounded-tasky-xs border border-warm-border"
                  style={{ background: template.gradient }}
                  title={template.gradient}
                />
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </TaskyTableCell>
            <TaskyTableCell>{template.sort_order}</TaskyTableCell>
            <TaskyTableCell>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${
                  template.is_active
                    ? 'bg-success/10 text-success'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {template.is_active ? '有効' : '無効'}
              </span>
            </TaskyTableCell>
            <TaskyTableCell className={`${STICKY_ACTIONS_CLASS} bg-white group-hover:bg-gray-50`}>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => onEdit(template)}
                  className="text-sm font-medium text-primary hover:text-primary-hover transition-colors"
                >
                  編集
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => onToggle(template.id)}
                  className={`text-sm font-medium transition-colors ${
                    template.is_active
                      ? 'text-gray-600 hover:text-dark'
                      : 'text-primary hover:text-primary-hover'
                  }`}
                >
                  {template.is_active ? '無効にする' : '有効にする'}
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => onDelete(template.id, template.name)}
                  className="text-sm font-medium text-destructive hover:text-destructive/80 transition-colors"
                >
                  削除
                </button>
              </div>
            </TaskyTableCell>
          </TaskyTableRow>
        ))}
      </TaskyTableBody>
    </TaskyTable>
  );
};

export default VideoTemplateList;
